/**
 * Global footer mounted under every route from `App.tsx`. Carries the
 * "Built by … Yonix" attribution per brand-identity §8.
 *
 * FEAT-019 (Session 8f.11) — right-aligned cross-links to the marketing
 * pages co-deployed at `/welcome`, `/coverage`, `/privacy`. On narrow
 * viewports the link list stacks below the brand credit instead of
 * sitting beside it.
 */
import { Logo } from './Logo';

interface FooterLink {
  readonly label: string;
  readonly href: string;
  readonly testId: string;
}

const LINKS: readonly FooterLink[] = [
  { label: 'About', href: '/welcome', testId: 'footer-about' },
  { label: 'Coverage', href: '/coverage', testId: 'footer-coverage' },
  { label: 'Privacy', href: '/privacy', testId: 'footer-privacy' },
];

export function AppFooter(): JSX.Element {
  return (
    <footer
      className="mt-auto flex flex-col items-center justify-between gap-3 border-t border-slate-800 bg-slate-950 px-4 py-3 text-xs text-slate-500 sm:flex-row sm:flex-wrap"
      data-testid="app-footer"
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Logo variant="mark" size="xs" testId="footer" />
        <span>
          Built by{' '}
          <a
            href="https://yonix.sk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-link hover:text-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
          >
            Michal Kurtulík · Yonix
          </a>
          . VirtualCharger.app — browser-only OCPP simulator.
        </span>
      </div>
      <nav
        aria-label="Site links"
        className="flex flex-wrap items-center justify-center gap-3 sm:justify-end"
      >
        {LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="text-text-secondary hover:text-primary-hover focus:outline-none focus:ring-2 focus:ring-primary-base rounded"
            data-testid={link.testId}
          >
            {link.label}
          </a>
        ))}
      </nav>
    </footer>
  );
}
